import { RequestDetailsHeader, RequestDetailsSection } from "../RequestDetailsContent";
import {
  RequestCardReason,
  RequestCardSection,
  RequestInfoItem,
  RequestInfoGrid,
  RequestCardHeader,
  RequestStatusStrip,
} from "../RequestCardContent";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { OnPremResponseProps, ResourceRequestsProps } from "../resourceRequestInterface";
import ResourceRequestToolbar from "../ResourceRequestToolbar";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";
import { changeStatus, checkNebulaStatus, getAdminNotificationAPI, getAdminNotificationCountAPI, revokeRequest, updateApp, updateFolder, updateGroupTypeStatus, updateUrl, updateWhiteswanAccess } from "../Api/resourcerequestapi";
import {
  isMultiApprovalRequest,
} from "../../MultiApprovals/Api/multiapprovalapi";
import MultiApprovalProgressPanel from "../../MultiApprovals/MultiApprovalProgressPanel";
import MultiApprovalGroupedHistory from "../../MultiApprovals/MultiApprovalGroupedHistory";
import MultiApprovalListSummary from "../../MultiApprovals/MultiApprovalListSummary";
import { useMultiApprovalWorkflow } from "../hooks/useMultiApprovalWorkflow";
import { format } from "date-fns";
import { Card, CardContent } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import {
  AlertTriangle,
  AppWindow,
  CheckCircle2,
  Clock,
  Eye,
  Folder,
  Link,
  Monitor,
  RotateCcw,
  Server,
  Shield,
  User,
  XCircle
} from "lucide-react";
import { Button } from "../../../ui/button";
import { getStatusColor } from "../utils";
import { Dialog, DialogContent } from "../../../ui/dialog";
import { Skeleton } from "../../interfaces/CommonInterfaces";
import { formatDate } from "../../formatTimeAgo";
import ConfirmationDialog from "../../Endpoints/ConfirmationDialog";
import { confirmNebulaChange } from "../../Endpoints/Api/EndpointApi";
import { RESOURCE_REQUEST_POLL_MS } from "../resourceRequestPolling";

export default function OnPremResourceRequests({
  computerName = "",
  searchTerm = "",
  statusFilter = "",
  privilegeFilter = "all",
  startDate,
  endDate,
  activeTab,
  onSearchChange
}: ResourceRequestsProps) {
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState<OnPremResponseProps[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<OnPremResponseProps | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<OnPremResponseProps | null>(null);
  const tenantName = useMemo(() => myLocalStorage.getItem("latestTenant")?.tenantName || "", []);
  const fetchInFlightRef = useRef(false);

  const { email, role } = useUserStore((state) => state.user);

  const {
    detailsWorkflow,
    openDetailsWorkflow,
    closeDetailsWorkflow,
    canUserApprove,
    canUserDecline,
    canUserDeclineDetails,
    getWorkflowForRequest,
  } = useMultiApprovalWorkflow(notifications);

  const fetchOnPremCount = useCallback(async () => {
    const response = await getAdminNotificationCountAPI(
      computerName,
      searchTerm,
      statusFilter.toUpperCase(),
      startDate ? format(startDate, "yyyy-MM-dd") : "",
      endDate ? format(endDate, "yyyy-MM-dd") : "",
      tenantName,
      page,
      email,
      role,
      privilegeFilter
    );
    setTotalItems(response || 0);
  }, [computerName, searchTerm, statusFilter, privilegeFilter, startDate, endDate, tenantName, page, email, role]);

  const fetchAdminDeviceOnPremNotification = useCallback(async (isFirst = true) => {
    if (isFirst) {
      setIsLoading(true);
    }
    try {
      const response: OnPremResponseProps[] = await getAdminNotificationAPI(
        computerName,
        searchTerm,
        statusFilter.toUpperCase(),
        startDate ? format(startDate, "yyyy-MM-dd") : "",
        endDate ? format(endDate, "yyyy-MM-dd") : "",
        tenantName,
        page - 1,
        email,
        role,
      privilegeFilter
    );
      setNotifications(response);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }, [computerName, searchTerm, statusFilter, privilegeFilter, startDate, endDate, tenantName, page, email, role]);

  const refreshRequests = useCallback(async (isFirst = true) => {
    if (!tenantName || !email || !role) {
      return;
    }
    if (fetchInFlightRef.current) {
      return;
    }

    fetchInFlightRef.current = true;
    try {
      await Promise.all([
        fetchOnPremCount(),
        fetchAdminDeviceOnPremNotification(isFirst),
      ]);
    } finally {
      fetchInFlightRef.current = false;
    }
  }, [tenantName, email, role, fetchOnPremCount, fetchAdminDeviceOnPremNotification]);

  const handleViewDetails = (request: OnPremResponseProps) => {
    setSelectedRequest(request);
    setIsDetailsOpen(true);
    openDetailsWorkflow(request);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "PENDING": return <Clock className="w-4 h-4" />;
      case "APPROVED": return <CheckCircle2 className="w-4 h-4" />;
      case "DECLINED": return <XCircle className="w-4 h-4" />;
      case "REVOKED": return <RotateCcw className="w-4 h-4" />;
      case "EXPIRED": return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category?.toUpperCase()) {
      case "SESSION": return <Monitor className="w-4 h-4 text-blue-500" />;
      case "APP": return <AppWindow className="w-4 h-4 text-purple-500" />;
      case "FOLDER": return <Folder className="w-4 h-4 text-yellow-500" />;
      case "URL": return <Link className="w-4 h-4 text-green-500" />;
      case "SYSTEM": return <Server className="w-4 h-4 text-gray-500" />;
      default: return <Monitor className="w-4 h-4 text-blue-500" />;
    }
  };

  const getCategoryIconLeft = (category: string) => {
    switch (category?.toUpperCase()) {
      case "SESSION": return <Monitor className="w-3.5 h-3.5 text-gray-500" />;
      case "APP": return <AppWindow className="w-3.5 h-3.5 text-gray-500" />;
      case "FOLDER": return <Folder className="w-3.5 h-3.5 text-gray-500" />;
      case "URL": return <Link className="w-3.5 h-3.5 text-gray-500" />;
      case "SYSTEM": return <Server className="w-3.5 h-3.5 text-gray-500" />;
      default: return <Monitor className="w-3.5 h-3.5 text-gray-500" />;
    }
  };


  const handleApproveClick = async (notification: any) => {
    const isNebulaDisabled = await checkNebulaStatus(notification.computerId);
    if (!isNebulaDisabled && notification.resourceName === "Whiteswan Access") {
      setSelectedNotification(notification);
      setOpenConfirmDialog(true);
    } else {
      handleRequest("APPROVED", notification.id, notification);

    }
  };


  const handleConfirmNebulaEnable = async () => {
    if (!selectedNotification) return;

    const success = await confirmNebulaChange({ deviceId: String(selectedNotification.computerId), enableNebula: true });
    if (success) {
      handleRequest("APPROVED", selectedNotification.id, selectedNotification);

    }
    setOpenConfirmDialog(false);
    setSelectedNotification(null);
  };


  const handleRequest = async (
    status: string,
    id: number,
    notification: OnPremResponseProps
  ) => {
    try {
      setLoadingId(id);
      const updatedRequest = await changeStatus(status, id) as OnPremResponseProps | undefined;

      if (status === "APPROVED" && updatedRequest?.requestStatus === "APPROVED") {
        await submitUpdateGroupStatus(notification);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingId(null);
    }

    await refreshRequests(false);
  };


  const updateWhiteswanAccessGroup = async (notification: OnPremResponseProps) => {
    const groupStatus = notification.selectedTime ? "DYNAMIC" : "ENABLED";

    try {
      let payload = {
        userId: notification.userId,
        computerId: notification.computerId,
        groupStatus,
        selectedTime: notification.selectedTime * 60,
      }

      await updateWhiteswanAccess(payload, notification.tenantName);
    } catch (error) {
      console.error(error);
    }
  };

  const updateGroupStatus = async (notification: OnPremResponseProps) => {
    const groupStatus = notification.selectedTime ? "DYNAMIC" : "ENABLED";

    const commonPayload = {
      userId: notification.userId,
      groupStatus,
      selectedTime: notification.selectedTime * 60,
      email: email,
      role: role,
      fromAccessRequest: true,
    };

    try {
      if (notification.resourceType === "GROUP") {

        let payload = {
          ...commonPayload,
          groupId: notification.resourceId,
        }
        await updateGroupTypeStatus(payload, notification.tenantName, notification.computerId)

      } else if (notification.resourceType === "FOLDER") {

        let payload = {
          ...commonPayload,
          folderId: notification.resourceId,
          computerId: notification.computerId,
        }

        await updateFolder(payload, notification.tenantName);
      } else if (notification.resourceType === "APP") {

        let payload = {
          ...commonPayload,
          appId: notification.resourceId,
          publisherId: notification.publisherId,
        }

        await updateApp(payload, notification.tenantName);

      } else if (notification.resourceType === "URL") {


        let payload = {
          ...commonPayload,
          urlId: notification.resourceId,
          computerId: notification.computerId,
        }

        await updateUrl(payload, notification.tenantName);
      }
    } catch (err) {
      console.error("Error updating group status:", err);
    }
  };



  const submitUpdateGroupStatus = async (notification: OnPremResponseProps) => {
    if (notification?.isWhiteswanAccess) {
      await updateWhiteswanAccessGroup(notification);
    } else {
      await updateGroupStatus(notification);
    }
  };


  const handleRevoke = async (accessId: number) => {
    await revokeRequest("onprem", accessId)
    await refreshRequests(false);
  }

  useEffect(() => {
    if (!tenantName || !email || !role) {
      return;
    }

    void refreshRequests(true);

    if (activeTab !== "onprem") {
      return;
    }

    const interval = setInterval(() => {
      void refreshRequests(false);
    }, RESOURCE_REQUEST_POLL_MS);

    return () => clearInterval(interval);
  }, [
    computerName,
    searchTerm,
    statusFilter,
    privilegeFilter,
    startDate,
    endDate,
    tenantName,
    page,
    email,
    role,
    activeTab,
    refreshRequests,
  ]);

  const totalPages = Math.ceil(totalItems / 15);
  return (
    <div className="resource-request-list space-y-1">
      {onSearchChange && (
        <ResourceRequestToolbar
          page={page}
          totalItems={totalItems}
          onPageChange={setPage}
          searchTerm={searchTerm}
          onSearchChange={(value) => {
            setPage(1);
            onSearchChange(value);
          }}
        />
      )}
      {isLoading ? <>
        {[...Array(1)].map((_, idx) => (
          <Card key={idx} className="request-card border-slate-200 dark:border-slate-700">
            <CardContent className="request-card-content p-4 animate-pulse">
              <div className="flex justify-between items-center mb-3">
                <Skeleton className="h-4 w-32 rounded" />
                <Skeleton className="h-6 w-20 rounded" />
              </div>
              <Skeleton className="h-3 w-full rounded mb-2" />
              <Skeleton className="h-3 w-3/4 rounded mb-2" />
              <Skeleton className="h-3 w-1/2 rounded" />
              <div className="mt-4 flex justify-between">
                <Skeleton className="h-6 w-28 rounded" />
                <Skeleton className="h-6 w-28 rounded" />
              </div>
            </CardContent>
          </Card>
        ))}
      </> : notifications.length > 0 ? (
        notifications.map((request) => (
          <Card
            key={request.id}
            className="request-card border-slate-200 dark:border-slate-700"
            data-testid={`onprem-request-card-${request.id}`}
          >
            <RequestStatusStrip status={request.requestStatus} />
            <CardContent className="request-card-content p-2">
              <div className="flex items-start gap-3">
                <div className="request-card-body flex-1 space-y-1">
                  {/* Header */}
                  <RequestCardHeader
                    icon={getCategoryIconLeft(request.resourceName)}
                    title={
                      <>
                        {request.resourceName === "Whiteswan Access"
                          ? "Connect Access"
                          : request.resourceName}
                      </>
                    }
                    status={
                      <Badge
                        className={`border text-xs px-1.5 py-0.5 ${getStatusColor(request.requestStatus)}`}
                      >
                        <div className="flex items-center gap-1">
                          {getStatusIcon(request.requestStatus)}
                          {request.requestStatus}
                        </div>
                      </Badge>
                    }
                    subtitle={
                      <p className="flex items-center gap-2 text-sm break-words text-slate-600 dark:text-slate-300">
                        <User className="w-4 h-4" />
                        {request.requestedBy}
                        <span className="text-slate-400">•</span>
                        {formatDate(request.createdAt)}
                      </p>
                    }
                    actions={
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(request)}
                        className="h-auto px-2 py-1 text-xs"
                        data-testid={`button-details-onprem-${request.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Details
                      </Button>
                    }
                  />

                  {/* Details Grid */}
                  <RequestInfoGrid className="grid-cols-1 md:grid-cols-3">
                    <RequestInfoItem label="Computer">
                      <p className="break-words font-medium text-slate-700 dark:text-slate-200">
                        {request.computerName}
                      </p>
                    </RequestInfoItem>
                    <RequestInfoItem label="Category">
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(request.resourceType)}
                        <p className="break-words font-medium text-slate-700 dark:text-slate-200">
                          {request.resourceType}
                        </p>
                      </div>
                    </RequestInfoItem>
                    {request.privilegeLevel === "PERMANENT" && (
                      <RequestInfoItem label="Access Type">
                        <p className="break-words  font-medium text-slate-700 dark:text-slate-200">
                          Permanent
                        </p>
                      </RequestInfoItem>
                    )}

                    {request.schedules.length === 0 && request.selectedTime !== 0 && (
                      <RequestInfoItem label="Duration">
                        <p className="text-slate-700 font-medium dark:text-slate-200">
                          {request.privilegeLevel === "PERMANENT"
                            ? "Permanent"
                            : `${request.selectedTime} min`}
                        </p>
                      </RequestInfoItem>
                    )}
                  </RequestInfoGrid>

                  {/* Connection Parameters */}
                  <RequestInfoGrid className="grid-cols-1 md:grid-cols-2" context>
                    {request.resourceType !== "GROUP" ? (
                      <>
                        <RequestInfoItem label="Machine User">
                          <p className=" break-words text-slate-700 dark:text-slate-200">
                            {request.username ? request.username : "-"}
                          </p>
                        </RequestInfoItem>
                      </>
                    ) : (
                      <>
                        {request.isManualDevice ? (
                          <RequestInfoItem label="Connection Via">
                            <p className=" break-words text-slate-700 dark:text-slate-200">
                              Configuration
                            </p>
                          </RequestInfoItem>
                        ) : (
                          !request.connectionLabel && (
                            <RequestInfoItem label="Connection Via">
                              <p className="break-words text-slate-700 dark:text-slate-200">
                                {request.machineUser
                                  ? "Machine User"
                                  : request.connectionMode === "generic"
                                    ? "JIT Access"
                                    : request.connectionMode
                                      ? request.connectionMode.replace("_", " ")
                                      : "Manual"}
                              </p>
                            </RequestInfoItem>
                          )
                        )}
                      </>
                    )}
                    {request.machineUser &&
                      request.machineUser !== "false" &&
                      request.resourceType === "GROUP" && (
                        <RequestInfoItem label="Machine Username">
                          <p className="break-words text-slate-700 dark:text-slate-200">
                            {request.machineUser}
                          </p>
                        </RequestInfoItem>
                      )}
                    {request.connectionUser && (
                      <RequestInfoItem label="Connection Username">
                        <p className="break-words text-slate-700 dark:text-slate-200">
                          {request.connectionUser}
                        </p>
                      </RequestInfoItem>
                    )}
                  </RequestInfoGrid>

                  {(() => {
                    const workflow = getWorkflowForRequest(request.id);
                    return isMultiApprovalRequest(workflow) ? (
                      <MultiApprovalListSummary workflow={workflow} />
                    ) : null;
                  })()}

                  {request.schedules?.length > 0 && (
                    <RequestCardSection variant="schedule">
                      <RequestInfoGrid className="grid-cols-2" context>
                        <RequestInfoItem label="Start Time">
                          <p className="text-blue-800 dark:text-blue-200">
                            {formatDate(request.schedules[0].scheduledStartTime)}
                          </p>
                        </RequestInfoItem>
                        <RequestInfoItem label="End Time">
                          <p className="text-blue-800 dark:text-blue-200">
                            {formatDate(request.schedules[0].scheduledEndTime)}
                          </p>
                        </RequestInfoItem>
                      </RequestInfoGrid>
                    </RequestCardSection>
                  )}
                  {((request.approvalEmailId?.length ?? 0) > 0 || request.revokedBy) && (
                    <RequestCardSection variant="history">
                      <div className="space-y-1 text-sm">
                        {request.approvalEmailId.length > 0 && (
                          <p className="text-green-700 dark:text-green-300">
                            ✓ Last Approved by {request.approvalEmailId[0]} on{" "}
                            {formatDate(request.createdAt)}
                          </p>
                        )}

                        {request.revokedBy && (
                          <p className="text-purple-700 dark:text-purple-300">
                            ⊝ Revoked by {request.revokedBy} on{" "}
                            {formatDate(
                              request.updatedAt ||
                                request.accessTimeOverAt ||
                                request.createdAt,
                            )}
                          </p>
                        )}
                      </div>
                    </RequestCardSection>
                  )}

                  {/* Actions */}
                  <div className="request-card-footer flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                    <RequestCardReason>{request.requestMessage}</RequestCardReason>
                    <div className="flex items-center gap-2">
                      {request.requestStatus === "PENDING" && (
                        <>
                          {role !== "TENANT_USER" ? (
                            <>
                              <Button
                                size="sm"
                                disabled={
                                  loadingId === request.id || !canUserApprove(request.id)
                                }
                                className="text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                onClick={() => {
                                  handleApproveClick(request);
                                }}
                              >
                                {loadingId === request.id ? (
                                  <>
                                    <svg
                                      className="animate-spin w-4 h-4 mr-1 text-white"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                      ></path>
                                    </svg>
                                    Approving...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                disabled={!canUserDecline(request.id)}
                                className="disabled:opacity-50"
                                onClick={() =>
                                  handleRequest("DECLINE", request.id, request)
                                }
                              >
                                <XCircle className="w-4 h-4 mr-1" /> Decline
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                className="text-white bg-green-600 hover:bg-green-700"
                                onClick={() => setIsDetailsOpen?.(false)}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                                onClick={() => {
                                  handleRevoke(request.id);
                                }}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />{" "}
                                {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}
                              </Button>
                            </>
                          )}
                        </>
                      )}

                      {request.requestStatus === "APPROVED" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                          onClick={() => {
                            handleRevoke(request.id);
                          }}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />{" "}
                          {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      ) : (
        <Card className="request-card request-empty-state border-slate-200 dark:border-slate-700">
          <CardContent className="request-card-content p-12 text-center">
            <Server className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
            <h3 className="mb-2 text-lg font-medium text-slate-600 dark:text-slate-300">No endpoint requests found</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No access requests found for endpoint resources
            </p>
          </CardContent>
        </Card>
      )
      }

      {!onSearchChange && totalPages > 1 && (
        <div className="flex items-center justify-between py-4">
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, totalItems)} of {totalItems}
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              data-testid="button-prev-page-onprem"
            >
              Previous
            </Button>

            {/* Dynamic Page Numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
              const pageNum = startPage + i;

              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={pageNum === page ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                  data-testid={`page-onprem-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}

            {/* Next Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              data-testid="button-next-page-onprem"
            >
              Next
            </Button>
          </div>
        </div>
      )}


      {/* Request Details Modal */}
      <Dialog
        open={isDetailsOpen}
        onOpenChange={(open) => {
          setIsDetailsOpen(open);
          if (!open) {
            closeDetailsWorkflow();
          }
        }}
      >
        <DialogContent className="request-details-dialog">
          <RequestDetailsHeader
            icon={<Shield className="w-5 h-5" />}
            title="On-Premises Access Request Details"
          >
            Complete information for request #{selectedRequest?.id}
          </RequestDetailsHeader>

          {selectedRequest && (
            <>
              <div className="request-details-body">
                <RequestDetailsSection title="Request information" variant="request">
                  <RequestInfoGrid className="request-details-grid">
                    <RequestInfoItem label="Request ID">
                      <p className="font-mono break-words text-slate-700 dark:text-slate-200">
                        {selectedRequest.id}
                      </p>
                    </RequestInfoItem>
                    <RequestInfoItem label="Status">
                      <Badge
                        className={`${getStatusColor(selectedRequest.requestStatus)} mt-1 `}
                      >
                        {selectedRequest.requestStatus}
                      </Badge>
                    </RequestInfoItem>
                    <RequestInfoItem label="Requested By">
                      <p className="break-words text-slate-700 dark:text-slate-200">
                        {selectedRequest.requestedBy}
                      </p>
                    </RequestInfoItem>
                    <RequestInfoItem label="Created At">
                      <p className="text-slate-700 dark:text-slate-200">
                        {formatDate(selectedRequest.createdAt)}
                      </p>
                    </RequestInfoItem>
                  </RequestInfoGrid>
                </RequestDetailsSection>
                <RequestDetailsSection title="Device and connection">
                  <RequestInfoGrid className="request-details-grid">
                    <RequestInfoItem label="Computer Name">
                      <p className="break-words text-slate-700 dark:text-slate-200">
                        {selectedRequest.computerName}
                      </p>
                    </RequestInfoItem>
                    {selectedRequest.isManualDevice ? (
                      <RequestInfoItem label="Connection Via">
                        <p className=" break-words text-slate-700 dark:text-slate-200">
                          Configuration
                        </p>
                      </RequestInfoItem>
                    ) : (
                      !selectedRequest.connectionLabel && (
                        <RequestInfoItem label="Connection Via">
                          <p className="break-words text-slate-700 dark:text-slate-200">
                            {selectedRequest.machineUser
                              ? "Machine User"
                              : selectedRequest.connectionMode === "generic"
                                ? "JIT Access"
                                : selectedRequest.connectionMode
                                  ? selectedRequest.connectionMode.replace("_", " ")
                                  : "Manual"}
                          </p>
                        </RequestInfoItem>
                      )
                    )}
                    {selectedRequest.machineUser &&
                      selectedRequest.machineUser !== "false" && (
                        <RequestInfoItem label="Machine Username">
                          <p className="break-words text-slate-700 dark:text-slate-200">
                            {selectedRequest.machineUser}
                          </p>
                        </RequestInfoItem>
                      )}
                    {selectedRequest.connectionUser && (
                      <RequestInfoItem label="Connection Username">
                        <p className="break-words text-slate-700 dark:text-slate-200">
                          {selectedRequest.connectionUser}
                        </p>
                      </RequestInfoItem>
                    )}
                  </RequestInfoGrid>
                </RequestDetailsSection>
                {/* Admin Action Details */}
                {detailsWorkflow?.groupedApprovals?.some(
                  (group) => group.approvals.length > 0,
                ) ? (
                  <div className="request-details-workflow">
                    <MultiApprovalGroupedHistory
                      groupedApprovals={detailsWorkflow.groupedApprovals}
                    />
                  </div>
                ) : selectedRequest.requestStatus === "APPROVED" ||
                  (selectedRequest.approvalEmailId?.length ?? 0) > 0 ? (
                  <div className="request-details-history">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-sm font-medium text-green-700 dark:text-green-300">
                        Approval Details
                      </span>
                    </div>
                    <RequestInfoGrid className="request-details-grid">
                      <RequestInfoItem label="Approved By">
                        <p className="text-green-700 dark:text-green-300">
                          {selectedRequest.approvalEmailId?.[0]}
                        </p>
                      </RequestInfoItem>
                      <RequestInfoItem label="Approved At">
                        <p className="text-green-700 dark:text-green-300">
                          {formatDate(
                            selectedRequest.updatedAt ??
                              selectedRequest.accessTimeOverAt ??
                              selectedRequest.createdAt,
                          )}
                        </p>
                      </RequestInfoItem>
                    </RequestInfoGrid>
                  </div>
                ) : null}
                {selectedRequest.requestStatus === "PENDING" &&
                  isMultiApprovalRequest(detailsWorkflow) && (
                    <div className="request-details-workflow">
                      <MultiApprovalProgressPanel
                        ruleProgress={detailsWorkflow.ruleProgress}
                      />
                    </div>
                  )}
                {selectedRequest.requestStatus === "DECLINED" && (
                  <div className="request-details-history">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <span className="text-sm font-medium text-red-700 dark:text-red-300">
                        Decline Details
                      </span>
                    </div>
                    <RequestInfoGrid className="request-details-grid">
                      <RequestInfoItem label="Declined At">
                        <p className="text-red-700 dark:text-red-300">
                          {formatDate(
                            selectedRequest.updatedAt ??
                              selectedRequest.accessTimeOverAt ??
                              selectedRequest.createdAt,
                          )}
                        </p>
                      </RequestInfoItem>
                    </RequestInfoGrid>
                  </div>
                )}
                {selectedRequest.requestStatus === "REVOKED" &&
                  selectedRequest.revokedBy && (
                    <div className="request-details-history">
                      <div className="flex items-center gap-2 mb-2">
                        <RotateCcw className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                          Revoke Details
                        </span>
                      </div>
                      <RequestInfoGrid className="request-details-grid">
                        <RequestInfoItem label="Revoked By">
                          <p className="text-purple-700 dark:text-purple-300">
                            {selectedRequest.revokedBy}
                          </p>
                        </RequestInfoItem>
                        <RequestInfoItem label="Revoked At">
                          <p className="text-purple-700 dark:text-purple-300">
                            {formatDate(
                              selectedRequest.updatedAt ??
                                selectedRequest.accessTimeOverAt ??
                                selectedRequest.createdAt,
                            )}
                          </p>
                        </RequestInfoItem>
                      </RequestInfoGrid>
                    </div>
                  )}
                {/* Business Justification */}
                <RequestDetailsSection
                  title="Business Justification"
                  variant="justification"
                >
                  <p className="request-details-message">
                    {selectedRequest.requestMessage}
                  </p>
                </RequestDetailsSection>
                {/* Admin Action Buttons */}
              </div>
              <div className="request-details-footer">
                {selectedRequest.requestStatus === "PENDING" ? (
                  role !== "TENANT_USER" ? (
                    <div className="request-details-actions">
                      <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
                        Close
                      </Button>
                      <Button
                        variant="destructive"
                        disabled={!canUserDeclineDetails()}
                        className="disabled:opacity-50"
                        onClick={() => {
                          handleRequest("DECLINE", selectedRequest.id, selectedRequest);
                          setIsDetailsOpen(false);
                        }}
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Decline
                      </Button>
                      <Button
                        className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        disabled={
                          loadingId === selectedRequest.id ||
                          !canUserApprove(selectedRequest.id)
                        }
                        onClick={() => {
                          handleApproveClick(selectedRequest);
                        }}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </div>
                  ) : (
                    <div className="request-details-actions">
                      <Button
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => setIsDetailsOpen(false)}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                      </Button>
                    </div>
                  )
                ) : null}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>


      <ConfirmationDialog
        open={openConfirmDialog}
        onOpenChange={setOpenConfirmDialog}
        title={"Enable Connectivity to Approve Request?"}
        description={"Currently connectivity is disabled for this device. Would you like to enable it and proceed with approval?"}
        confirmText="Confirm"
        cancelText="Cancel"
        variant={"warning"}
        onConfirm={() => {
          handleConfirmNebulaEnable();
          setOpenConfirmDialog(false);
          setSelectedNotification(null);
        }}
      />

    </div >
  );
}
